import { auth, db, functions } from "@/firebase";
import type { ConfigMetadata, MethodValidation } from "@/state/validationState";
import type { SystemConfig } from "@/state/configState";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { parseFullConfigXml } from "./systemConfigParser";
import { isLegacyConfig, enrichLegacyLabware } from "./legacyConfigCompat";

/**
 * Fetches all system configurations the user has access to across all workspaces they're a member of
 *
 * @param workspaceId - The ID of the workspace (used as a starting point for permission checks)
 * @returns A promise that resolves to an array of configuration metadata
 */
export async function fetchUserConfigs(
  workspaceId: string
): Promise<ConfigMetadata[]> {
  try {
    const getUserConfigs = httpsCallable<
      { workspaceId: string },
      ConfigMetadata[]
    >(functions, "getUserConfigsV2");

    const result = await getUserConfigs({ workspaceId });
    return result.data;
  } catch (error) {
    console.error("Error fetching user configs:", error);
    throw new Error("Failed to fetch user configurations");
  }
}

/**
 * Loads a specific system configuration from a workspace
 *
 * @param workspaceId - The ID of the workspace
 * @param configName - The name/ID of the configuration to load
 * @returns A promise that resolves to the system configuration
 */
export async function loadSystemConfig(
  workspaceId: string,
  configName: string
): Promise<SystemConfig> {
  try {
    const configRef = doc(
      db,
      `workspaces/${workspaceId}/system_configs/${configName}`
    );
    const configDoc = await getDoc(configRef);

    if (!configDoc.exists()) {
      throw new Error(`System configuration not found: ${configName}`);
    }

    const data = configDoc.data();
    const parsedConfig = parseFullConfigXml(data.configText);

    // LEGACY_COMPAT: Enrich old configs that lack structured labware metadata
    if (isLegacyConfig(parsedConfig.labware)) {
      enrichLegacyLabware(parsedConfig.labware);
    }

    return {
      name: data.name || configName,
      configText: data.configText,
      createdAt: data.createdAt,
      createdBy: data.createdBy,
      workspaceId: data.workspaceId,
      robots: parsedConfig.robots,
      labware: parsedConfig.labware,
      heads: parsedConfig.heads,
      steps: parsedConfig.steps,
      deckLayouts: parsedConfig.deckLayouts,
      liquidClasses: parsedConfig.liquidClasses,
    };
  } catch (error) {
    console.error("Error loading system config:", error);
    throw new Error(`Failed to load system configuration: ${configName}`);
  }
}

/**
 * Gets the validation config for a method along with the full system config if available
 * @param workspaceId - The ID of the workspace
 * @param methodId - The ID of the method
 * @returns A promise that resolves to the method validation with system config if available
 */
export async function getMethodValidationConfig(
  workspaceId: string,
  methodId: string
): Promise<{ validation: MethodValidation | null; config?: SystemConfig }> {
  try {
    const methodRef = doc(db, `workspaces/${workspaceId}/methods/${methodId}`);
    const methodDoc = await getDoc(methodRef);

    if (!methodDoc.exists()) {
      throw new Error("Method not found");
    }

    const data = methodDoc.data();
    const validation = data.validation || null;

    // If there's a validation config ID, load the full config
    let config: SystemConfig | undefined;
    if (validation?.validationConfigId) {
      try {
        config = await loadSystemConfig(
          workspaceId,
          validation.validationConfigId
        );
      } catch (error) {
        console.error("Error loading validation config content:", error);
      }

      // Lazy migration: if config not found and was the old "auto" monolithic config,
      // try to find any per-head auto-generated config as a fallback
      if (!config && validation.validationConfigId === "auto") {
        try {
          const snap = await getDocs(
            collection(db, `workspaces/${workspaceId}/system_configs`),
          );
          const autoConfig = snap.docs.find((d) => d.id.startsWith("auto__"));
          if (autoConfig) {
            config = await loadSystemConfig(workspaceId, autoConfig.id);
            // Update method to point to the new per-head config
            await updateDoc(
              doc(db, `workspaces/${workspaceId}/methods/${methodId}`),
              { "validation.validationConfigId": autoConfig.id },
            );
          }
        } catch (migrationError) {
          console.error("Error during auto config migration:", migrationError);
        }
      }
    }

    return { validation, config };
  } catch (error) {
    console.error("Error getting method validation config:", error);
    throw error;
  }
}

/**
 * Duplicates a system configuration from one workspace to another
 * @param fromWorkspaceId - The source workspace ID
 * @param toWorkspaceId - The destination workspace ID
 * @param configName - The name of the configuration to duplicate
 * @param newConfigName - Optional new name for the duplicated config (defaults to original name)
 * @returns A promise that resolves to the duplicated system configuration
 */
export async function duplicateSystemConfig(
  fromWorkspaceId: string,
  toWorkspaceId: string,
  configName: string,
  newConfigName?: string
): Promise<SystemConfig> {
  try {
    // Load the original config
    const originalConfig = await loadSystemConfig(fromWorkspaceId, configName);

    // Use new name if provided, otherwise use original name
    const targetConfigName = newConfigName || configName;

    // Create the new config in the destination workspace
    const newConfigRef = doc(
      db,
      `workspaces/${toWorkspaceId}/system_configs/${targetConfigName}`
    );

    const currentUid = auth.currentUser?.uid;
    if (!currentUid) {
      throw new Error("Not authenticated");
    }
    await setDoc(newConfigRef, {
      name: targetConfigName,
      configText: originalConfig.configText,
      createdAt: new Date(),
      // Firestore rule requires createdBy == request.auth.uid on system_configs
      createdBy: currentUid,
      workspaceId: toWorkspaceId,
    });

    // Return the duplicated config with updated metadata
    return {
      ...originalConfig,
      name: targetConfigName,
      workspaceId: toWorkspaceId,
      createdAt: new Date(),
    };
  } catch (error) {
    console.error("Error duplicating system config:", error);
    throw new Error(`Failed to duplicate system configuration: ${configName}`);
  }
}

/**
 * Connects a method to a validation config
 */
export async function connectMethodToConfig(
  workspaceId: string,
  methodId: string,
  configId: string
) {
  try {
    const methodRef = doc(db, `workspaces/${workspaceId}/methods/${methodId}`);

    await updateDoc(methodRef, {
      validation: {
        validationConfigId: configId,
        lastValidated: null,
        isValid: null,
      },
    });

    return true;
  } catch (error) {
    console.error("Error connecting method to config:", error);
    throw error;
  }
}
